import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, Text } from '@bonusly/design-system';
import { emit } from '@bonusly/events';

import { GiveBoxEvent } from '../../recognition/give-box/events';
import { createDraftContent } from '../../recognition/give-box/utils/content';
import type { ApprovalDecision } from '../hooks/use-bizy-global-chat';
import { BizyRecognitionInline } from './bizy-recognition-inline';

export interface DraftVariant {
	tone: string;
	recipients?: string[];
	amount?: number;
	message?: string;
	hashtags?: string[];
}

export interface BizyDraftCardProps {
	variants: DraftVariant[];
	confirmLabel?: string;
	/**
	 * Persisted choice for this block. When set, both action buttons are
	 * disabled so the user can't trigger a duplicate send/open after their
	 * first interaction -- including after a conversation refetch where the
	 * choice was hydrated from history. The card itself stays visible (preview
	 * and tone tabs) by design; only the buttons lock.
	 */
	choice?: ApprovalDecision;
	isDisabled?: boolean;
	onConfirm?: (selectedVariant: DraftVariant) => void;
	/** Called when user clicks "Give Now" -- sends the confirm message back to Bizy. */
	onGiveNow?: (confirmMessage: string) => void;
}

function buildConfirmMessage(variant: DraftVariant): string {
	const parts: string[] = [];
	if (variant.recipients?.length) parts.push(variant.recipients.map((r) => `@${r}`).join(' '));
	if (variant.amount) parts.push(`+${variant.amount}`);
	if (variant.message) parts.push(variant.message);
	if (variant.hashtags?.length) parts.push(variant.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' '));
	return `Yes, send it: ${parts.join(' ')}`;
}

function ToneTab({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void }) {
	return (
		<Box
			as="button"
			role="tab"
			aria-selected={isActive}
			onClick={onClick}
			px={3}
			py={1.5}
			fontSize="xs"
			fontWeight="medium"
			borderRadius={8}
			cursor="pointer"
			transition="all 0.15s ease"
			bg={isActive ? 'bg.surface' : 'transparent'}
			color={isActive ? 'text.default' : 'text.softer'}
			boxShadow={isActive ? 'sm' : 'none'}
			_hover={{ color: 'text.default' }}
		>
			{label}
		</Box>
	);
}

function RecognitionPreview({ variant }: { variant: DraftVariant }) {
	return (
		<Text fontSize="sm" color="text.default" lineHeight="tall">
			<BizyRecognitionInline recipients={variant.recipients} amount={variant.amount} message={variant.message} hashtags={variant.hashtags} />
		</Text>
	);
}

export function BizyDraftCard({ variants, confirmLabel, choice: externalChoice, isDisabled = false, onConfirm, onGiveNow }: BizyDraftCardProps) {
	const { t } = useTranslation('admin_bizy');
	const [activeIndex, setActiveIndex] = useState(0);
	// Local mirror lets us lock buttons immediately on click without waiting
	// for the parent to round-trip the persisted choice back down. External
	// choice (from hydrated history) wins so reloads stay locked.
	const [localChoice, setLocalChoice] = useState<ApprovalDecision | undefined>();
	const choice = externalChoice ?? localChoice;
	const isLocked = Boolean(choice) || isDisabled;

	const activeVariant = variants[activeIndex] ?? variants[0];

	const handleEditInGiveBox = useCallback(() => {
		if (isLocked) return;
		setLocalChoice('approved');
		const recipients = activeVariant.recipients ?? [];
		const hashtags = activeVariant.hashtags?.map((h) => (h.startsWith('#') ? h.slice(1) : h));
		const editorContent = createDraftContent(recipients, activeVariant.message, activeVariant.amount, hashtags, { aiGenerated: true });
		emit(GiveBoxEvent.Open, { editorContent });
		onConfirm?.(activeVariant);
	}, [activeVariant, isLocked, onConfirm]);

	const handleGiveNow = useCallback(() => {
		if (isLocked) return;
		setLocalChoice('approved');
		const confirmMessage = buildConfirmMessage(activeVariant);
		onConfirm?.(activeVariant);
		if (confirmMessage) {
			onGiveNow?.(confirmMessage);
		}
	}, [activeVariant, isLocked, onConfirm, onGiveNow]);

	if (!variants.length) return null;

	return (
		<motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
			<Box border="1px solid" borderColor="border.default" borderRadius={12} bg="bg.surface" overflow="hidden">
				{variants.length > 1 && (
					<Flex role="tablist" gap={1} px={2} pt={2} pb={1}>
						<Flex bg="bg.subtle" borderRadius={10} p={0.5} gap={0.5}>
							{variants.map((v, i) => (
								<ToneTab key={v.tone} label={v.tone} isActive={i === activeIndex} onClick={() => setActiveIndex(i)} />
							))}
						</Flex>
					</Flex>
				)}

				<Box px={3} py={3}>
					<RecognitionPreview variant={activeVariant} />
				</Box>

				<Flex align="center" justify="flex-end" gap={2} px={3} pb={3}>
					<Button size="sm" variant="outline" colorScheme="secondary" onClick={handleGiveNow} isDisabled={isLocked} flexShrink={0}>
						{t('draft_card.give_now')}
					</Button>
					<Button size="sm" onClick={handleEditInGiveBox} isDisabled={isLocked} flexShrink={0}>
						{confirmLabel ?? t('draft_card.edit_in_give_box')}
					</Button>
				</Flex>
			</Box>
		</motion.div>
	);
}
